import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map(); // eventName -> Set of callbacks
    this.connectionAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.pingInterval = null;
  }

  /**
   * Initialize WebSocket connection
   * @param {Object} options - Connection options
   */
  initialize(options = {}) {
    const {
      serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000',
      autoConnect = true,
    } = options;

    if (this.socket) {
      this.disconnect();
    }

    try {
      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (!token) {
        console.warn('No authentication token found. WebSocket connection will not be established.');
        return false;
      }

      this.socket = io(serverUrl, {
        auth: {
          token: token
        },
        transports: ['websocket', 'polling'],
        autoConnect,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        timeout: 20000,
      });

      this.setupEventHandlers();
      
      if (autoConnect) {
        this.connect();
      }

      console.log('✅ WebSocket service initialized');
      return true;
      
    } catch (error) {
      console.error('❌ Failed to initialize WebSocket service:', error);
      return false;
    }
  }

  /**
   * Setup Socket.io event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.connectionAttempts = 0;
      console.log('🔌 WebSocket connected');
      
      // Start ping interval for connection health check
      this.startPingInterval();
      
      // Emit custom connect event
      this.emitToListeners('websocket_connected', { timestamp: new Date() });
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('🔌 WebSocket disconnected:', reason);
      
      // Stop ping interval
      this.stopPingInterval();
      
      // Emit custom disconnect event
      this.emitToListeners('websocket_disconnected', { reason, timestamp: new Date() });
      
      // Attempt reconnection for certain reasons
      if (reason === 'io server disconnect' || reason === 'transport close') {
        this.scheduleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔌 WebSocket connection error:', error.message);
      this.connectionAttempts++;
      
      // Emit custom error event
      this.emitToListeners('websocket_error', { error: error.message, attempts: this.connectionAttempts });
      
      if (this.connectionAttempts >= this.maxReconnectAttempts) {
        console.error('🔌 Max reconnection attempts reached. Giving up.');
        this.emitToListeners('websocket_max_reconnect_exceeded', { attempts: this.connectionAttempts });
      }
    });

    // Notification events
    this.socket.on('new_notification', (notification) => {
      console.log('🔔 New notification received via WebSocket:', notification);
      this.emitToListeners('new_notification', notification);
    });

    this.socket.on('unread_count_update', (data) => {
      console.log('📊 Unread count update:', data);
      this.emitToListeners('unread_count_update', data);
    });

    // Acknowledgment events
    this.socket.on('notification_read_ack', (data) => {
      this.emitToListeners('notification_read_ack', data);
    });

    this.socket.on('notification_clicked_ack', (data) => {
      this.emitToListeners('notification_clicked_ack', data);
    });

    this.socket.on('notification_dismissed_ack', (data) => {
      this.emitToListeners('notification_dismissed_ack', data);
    });

    // Room subscription events
    this.socket.on('room_subscribed', (data) => {
      console.log('📢 Subscribed to room:', data.room);
      this.emitToListeners('room_subscribed', data);
    });

    this.socket.on('room_unsubscribed', (data) => {
      console.log('📭 Unsubscribed from room:', data.room);
      this.emitToListeners('room_unsubscribed', data);
    });

    // Ping/Pong for connection health
    this.socket.on('pong', (data) => {
      const latency = Date.now() - data.timestamp;
      this.emitToListeners('websocket_pong', { latency, timestamp: new Date() });
    });
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (!this.socket) {
      console.error('WebSocket not initialized. Call initialize() first.');
      return;
    }

    if (!this.isConnected) {
      this.socket.connect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.stopPingInterval();
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket = null;
    }
    this.isConnected = false;
    this.listeners.clear();
  }

  /**
   * Add event listener
   * @param {string} eventName - Event name
   * @param {Function} callback - Callback function
   */
  addEventListener(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} eventName - Event name
   * @param {Function} callback - Callback function
   */
  removeEventListener(eventName, callback) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).delete(callback);
      
      // Remove empty event sets
      if (this.listeners.get(eventName).size === 0) {
        this.listeners.delete(eventName);
      }
    }
  }

  /**
   * Emit event to all registered listeners
   * @param {string} eventName - Event name
   * @param {*} data - Event data
   */
  emitToListeners(eventName, data) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket listener for ${eventName}:`, error);
        }
      });
    }
  }

  /**
   * Track notification as read
   * @param {string} notificationId - Notification ID
   * @param {Object} metadata - Additional metadata
   */
  trackNotificationRead(notificationId, metadata = {}) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot track notification read.');
      return;
    }

    this.socket.emit('notification_read', {
      notificationId,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Track notification click/interaction
   * @param {string} notificationId - Notification ID
   * @param {string} actionType - Type of action (click, view, etc.)
   * @param {Object} metadata - Additional metadata
   */
  trackNotificationClicked(notificationId, actionType = 'click', metadata = {}) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot track notification click.');
      return;
    }

    this.socket.emit('notification_clicked', {
      notificationId,
      actionType,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Track notification dismissal
   * @param {string} notificationId - Notification ID
   * @param {Object} metadata - Additional metadata
   */
  trackNotificationDismissed(notificationId, metadata = {}) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot track notification dismiss.');
      return;
    }

    this.socket.emit('notification_dismiss', {
      notificationId,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Subscribe to a room for targeted notifications
   * @param {string} room - Room name
   */
  subscribeToRoom(room) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot subscribe to room.');
      return;
    }

    this.socket.emit('subscribe_room', { room });
  }

  /**
   * Unsubscribe from a room
   * @param {string} room - Room name
   */
  unsubscribeFromRoom(room) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot unsubscribe from room.');
      return;
    }

    this.socket.emit('unsubscribe_room', { room });
  }

  /**
   * Start ping interval for connection health check
   */
  startPingInterval() {
    this.stopPingInterval(); // Clear any existing interval
    
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.socket.emit('ping');
      }
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.connectionAttempts < this.maxReconnectAttempts) {
      const delay = Math.min(1000 * Math.pow(2, this.connectionAttempts), 30000); // Exponential backoff
      
      this.reconnectTimeout = setTimeout(() => {
        console.log(`🔄 Attempting to reconnect... (${this.connectionAttempts + 1}/${this.maxReconnectAttempts})`);
        this.connect();
      }, delay);
    }
  }

  /**
   * Get connection status
   * @returns {Object} Connection information
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      connectionAttempts: this.connectionAttempts,
      maxAttempts: this.maxReconnectAttempts,
      hasSocket: !!this.socket,
      listenerCount: this.listeners.size,
    };
  }

  /**
   * Update authentication token
   * @param {string} newToken - New auth token
   */
  updateAuthToken(newToken) {
    if (this.socket) {
      this.socket.auth.token = newToken;
      
      // Reconnect with new token if currently connected
      if (this.isConnected) {
        this.socket.disconnect().connect();
      }
    }
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;